/**
 * Filter button, filter panel and the active-filter chips above the rides
 * list and calendar (docs/RIDE_SOURCES_UI_PLAN.md §4.4). Source lists only
 * providers the rider has rides from; Power is for rides only.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LucideIcon } from './icons/LucideIcon.tsx';
import { providerLabel, type Provider } from '../lib/providers.ts';
import type { RideScope } from '../hooks/useRides.ts';
import {
  NO_FILTERS,
  isFiltered,
  type PowerFilter,
  type RideFilters,
  type WhereFilter,
} from '../hooks/useRideFilters.ts';

const WHERE: readonly WhereFilter[] = ['all', 'indoor', 'outdoor'];
const POWER: readonly PowerFilter[] = ['all', 'with', 'without'];

const chipClass = (on: boolean) =>
  `px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
    on
      ? 'bg-(--color-accent)/15 text-(--color-accent) border-(--color-accent)/40'
      : 'bg-(--color-bg-tertiary) text-(--color-text-secondary) border-(--color-border) hover:border-gray-400'
  }`;

export function RideFiltersBar({ filters, onChange, providers, scope }: Readonly<{
  filters: RideFilters;
  onChange: (next: RideFilters) => void;
  providers: Provider[];
  scope: RideScope;
}>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const active = isFiltered(filters);
  return (
    <div className="mb-4 flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={`${chipClass(active)} inline-flex items-center gap-1.5`}
        >
          <LucideIcon name="sliders-horizontal" size={14} />
          {t('sources.filter.button')}
          {active && <span className="w-1.5 h-1.5 rounded-full bg-(--color-accent)" data-testid="filter-dot" />}
        </button>
        {active && <ActiveChips filters={filters} onChange={onChange} />}
      </div>
      {open && (
        <FilterPanel
          filters={filters}
          providers={providers}
          scope={scope}
          onApply={(next) => {
            onChange(next);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

function ActiveChips({ filters, onChange }: Readonly<{ filters: RideFilters; onChange: (next: RideFilters) => void }>) {
  const { t } = useTranslation();
  const remove = t('sources.filter.remove');
  return (
    <>
      {filters.providers.map((p) => (
        <button key={p} type="button" className={chipClass(true)} aria-label={`${remove}: ${providerLabel(p, t)}`}
          onClick={() => onChange({ ...filters, providers: filters.providers.filter((x) => x !== p) })}>
          {providerLabel(p, t)} ×
        </button>
      ))}
      {filters.where !== 'all' && (
        <button type="button" className={chipClass(true)} onClick={() => onChange({ ...filters, where: 'all' })}>
          {t(`sources.filter.where.${filters.where}`)} ×
        </button>
      )}
      {filters.power !== 'all' && (
        <button type="button" className={chipClass(true)} onClick={() => onChange({ ...filters, power: 'all' })}>
          {t(`sources.filter.power.${filters.power}`)} ×
        </button>
      )}
      <button type="button" className="text-xs text-(--color-accent) hover:underline" onClick={() => onChange(NO_FILTERS)}>
        {t('sources.filter.clear')}
      </button>
    </>
  );
}

function FilterPanel({ filters, providers, scope, onApply }: Readonly<{
  filters: RideFilters;
  providers: Provider[];
  scope: RideScope;
  onApply: (next: RideFilters) => void;
}>) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<RideFilters>(filters);
  const toggle = (p: Provider) =>
    setDraft({
      ...draft,
      providers: draft.providers.includes(p) ? draft.providers.filter((x) => x !== p) : [...draft.providers, p],
    });
  return (
    <section aria-label={t('sources.filter.title')}
      className="rounded-xl bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-3 text-sm">
      <h3 className="font-semibold">{t('sources.filter.title')}</h3>
      {providers.length > 0 && (
        <FilterRow label={t('sources.filter.source')}>
          <button type="button" aria-pressed={draft.providers.length === 0} className={chipClass(draft.providers.length === 0)}
            onClick={() => setDraft({ ...draft, providers: [] })}>
            {t('sources.filter.all')}
          </button>
          {providers.map((p) => (
            <button key={p} type="button" aria-pressed={draft.providers.includes(p)}
              className={chipClass(draft.providers.includes(p))} onClick={() => toggle(p)}>
              {providerLabel(p, t)}
            </button>
          ))}
        </FilterRow>
      )}
      <FilterRow label={t('sources.filter.whereLabel')}>
        {WHERE.map((w) => (
          <button key={w} type="button" aria-pressed={draft.where === w} className={chipClass(draft.where === w)}
            onClick={() => setDraft({ ...draft, where: w })}>
            {t(`sources.filter.where.${w}`)}
          </button>
        ))}
      </FilterRow>
      {scope === 'rides' && (
        <FilterRow label={t('sources.filter.powerLabel')}>
          {POWER.map((p) => (
            <button key={p} type="button" aria-pressed={draft.power === p} className={chipClass(draft.power === p)}
              onClick={() => setDraft({ ...draft, power: p })}>
              {t(`sources.filter.power.${p}`)}
            </button>
          ))}
        </FilterRow>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" className="px-4 py-1.5 rounded-lg text-sm border border-(--color-border)"
          onClick={() => setDraft(NO_FILTERS)}>
          {t('sources.filter.clear')}
        </button>
        <button type="button" className="px-4 py-1.5 rounded-lg text-sm font-semibold bg-green-400 text-gray-900 hover:bg-green-300"
          onClick={() => onApply(draft)}>
          {t('sources.filter.show')}
        </button>
      </div>
    </section>
  );
}

function FilterRow({ label, children }: Readonly<{ label: string; children: React.ReactNode }>) {
  return (
    <fieldset className="flex flex-wrap items-center gap-2 border-0 p-0 m-0">
      <legend className="w-full text-xs text-(--color-text-secondary) uppercase tracking-wide mb-1">{label}</legend>
      {children}
    </fieldset>
  );
}
